import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bot, Settings, History, Home, Menu, X, LogOut, UserPlus, Moon, Sun, MessageCircle, Users, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import UserProfileModal from './UserProfileModal';
import './Header.css';

const Header = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const { user, logout, isAdmin, canManageUsers, isGroupAdmin } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const isActive = (path) => location.pathname === path;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleLogout = () => {
    logout();
    setSidebarOpen(false);
  };

  return (
    <>
      {/* Botão Hambúrguer para Mobile */}
      <button 
        className="mobile-menu-button"
        onClick={toggleSidebar}
        aria-label="Abrir menu"
      >
        <Menu size={24} />
      </button>

      {/* Overlay para Mobile */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={toggleSidebar}
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* Botão de Fechar para Mobile */}
        <button 
          className="sidebar-close-button"
          onClick={toggleSidebar}
          aria-label="Fechar menu"
        >
          <X size={24} />
        </button>

        <div className="sidebar-brand">
          <div className="brand-icon">
            <Bot size={24} />
          </div>
            <h1 className="brand-title" style={{ fontSize: '20px' }}>Sistema de Notificações</h1>
        </div>
        
        <nav className="sidebar-nav">
          {/* Menu para Administradores */}
          {isAdmin ? (
            <>
              <Link 
                to="/" 
                className={`nav-item ${isActive('/') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <Home size={20} />
                </div>
                <span className="nav-label">Dashboard</span>
              </Link>
              
              <Link 
                to="/configuracoes" 
                className={`nav-item ${isActive('/configuracoes') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <Settings size={20} />
                </div>
                <span className="nav-label">Configurações</span>
              </Link>
              
              <Link 
                to="/historico" 
                className={`nav-item ${isActive('/historico') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <History size={20} />
                </div>
                <span className="nav-label">Histórico</span>
              </Link>
              
              <Link 
                to="/atendimento" 
                className={`nav-item ${isActive('/atendimento') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <MessageCircle size={20} />
                </div>
                <span className="nav-label">Atendimento</span>
              </Link>

              {/* Separador */}
              <div className="nav-separator"></div>

              {/* Toggle de tema */}
              <div className="theme-toggle-container">
                <div className="theme-toggle-label">
                  <div className="nav-icon">
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                  </div>
                  <span>Tema</span>
                </div>
                <div className="theme-switch" onClick={toggleTheme}>
                  <div className={`switch-track ${isDark ? 'dark' : 'light'}`}>
                    <div className="switch-thumb">
                      {isDark ? <Sun size={12} /> : <Moon size={12} />}
                    </div>
                  </div>
                  <span className="switch-label">
                    {isDark ? 'Escuro' : 'Claro'}
                  </span>
                </div>
              </div>

              {/* Opções de administração */}
              <Link 
                to="/manage-users" 
                className={`nav-item ${isActive('/manage-users') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <Users size={20} />
                </div>
                <span className="nav-label">Gerenciar Usuários</span>
              </Link>

              <Link 
                to="/manage-groups" 
                className={`nav-item ${isActive('/manage-groups') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <Users size={20} />
                </div>
                <span className="nav-label">Grupos</span>
              </Link>

              <Link 
                to="/mensagens-predefinidas" 
                className={`nav-item ${isActive('/mensagens-predefinidas') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <Zap size={20} />
                </div>
                <span className="nav-label">Mensagens Rápidas</span>
              </Link>

              <Link 
                to="/register" 
                className={`nav-item ${isActive('/register') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <UserPlus size={20} />
                </div>
                <span className="nav-label">Cadastrar Usuário</span>
              </Link>

              {/* Botão de logout */}
              <button 
                className="nav-item logout-button"
                onClick={handleLogout}
              >
                <div className="nav-icon">
                  <LogOut size={20} />
                </div>
                <span className="nav-label">Sair</span>
              </button>
            </>
          ) : (
            /* Menu para Usuários Comuns / Diretores de grupo */
            <>
              <Link 
                to="/atendimento" 
                className={`nav-item ${isActive('/atendimento') ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="nav-icon">
                  <MessageCircle size={20} />
                </div>
                <span className="nav-label">Atendimento</span>
              </Link>

              {canManageUsers && (
                <>
                  <div className="nav-separator"></div>
                  <Link 
                    to="/manage-users" 
                    className={`nav-item ${isActive('/manage-users') ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <div className="nav-icon">
                      <Users size={20} />
                    </div>
                    <span className="nav-label">Gerenciar Equipe</span>
                  </Link>
                  <Link 
                    to="/register" 
                    className={`nav-item ${isActive('/register') ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <div className="nav-icon">
                      <UserPlus size={20} />
                    </div>
                    <span className="nav-label">Cadastrar Usuário</span>
                  </Link>
                </>
              )}

              {/* Separador */}
              <div className="nav-separator"></div>

              {/* Toggle de tema */}
              <div className="theme-toggle-container">
                <div className="theme-toggle-label">
                  <div className="nav-icon">
                    {isDark ? <Sun size={18} /> : <Moon size={18} />}
                  </div>
                  <span>Tema</span>
                </div>
                <div className="theme-switch" onClick={toggleTheme}>
                  <div className={`switch-track ${isDark ? 'dark' : 'light'}`}>
                    <div className="switch-thumb">
                      {isDark ? <Sun size={12} /> : <Moon size={12} />}
                    </div>
                  </div>
                  <span className="switch-label">
                    {isDark ? 'Escuro' : 'Claro'}
                  </span>
                </div>
              </div>

              {/* Botão de logout */}
              <button 
                className="nav-item logout-button"
                onClick={handleLogout}
              >
                <div className="nav-icon">
                  <LogOut size={20} />
                </div>
                <span className="nav-label">Sair</span>
              </button>
            </>
          )}
            </nav>

            {/* Informações do usuário */}
            <div className="user-info" onClick={() => setShowProfileModal(true)}>
              <div className="user-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <div className="user-name">
                  {user?.full_name || user?.username || 'Usuário'}
                </div>
                <div className="user-role">
                  {user?.is_admin ? 'Administrador' : (isGroupAdmin ? 'Diretor de setor' : 'Usuário')}
                </div>
              </div>
            </div>
          </aside>

          {/* Modal de Perfil */}
          <UserProfileModal 
            isOpen={showProfileModal} 
            onClose={() => setShowProfileModal(false)} 
          />
    </>
  );
};

export default Header;
